import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DialogService } from '@app/old-ui/dialog/dialog.service';
import { Client, Device } from '@models';
import { Subscription } from 'rxjs';
import { FragQueryService } from 'src/injectables/frag-query.service';

interface TargetSizeRange {
    targetMinPr: number;
    targetMaxPr: number;
    targetSize: number;
}

interface MaterialTargetConfig {
    materialType: string;
    preferCustomSizes: boolean;
    targetSizes: TargetSizeRange[];
}

interface ClientMaterialType {
    text: string;
    color?: string;
}

@Component({
    selector: 'target-interval-settings',
    templateUrl: './target-intervals.component.html',
    styleUrls: ['./target-intervals.component.scss']
})
export class TargetIntervalsComponent implements OnChanges, OnDestroy {
    constructor(
        private fragQuery: FragQueryService,
        private readonly dialog: DialogService,
        private readonly snackBar:MatSnackBar
    ) {
        this.subscriptions.add(
            this.fragQuery.client$.subscribe(client => {
                this.loggedInClient = client;
                this.updateMaterialTypeOptions();
            })
        );
        this.subscriptions.add(
            this.fragQuery.clients$.subscribe(clients => {
                this.clients = clients ?? [];
                this.updateMaterialTypeOptions();
            })
        );
    }
    @Input() model: Device;
    @Input() saveFn!: (model: Device) => Promise<boolean>;
    @Input() visibleDeviceDropDown : boolean
    public readonly devices$ = this.fragQuery.devices$;
    public readonly device$ = this.fragQuery.device$;
    public readonly deviceUuid$ = this.fragQuery.deviceUuid$;
    public readonly particleSizeUnitSystem$ = this.fragQuery.particleSizeUnitSystem$;
    private readonly subscriptions = new Subscription();
    private loggedInClient: Client;
    private clients: Client[] = [];
    public newTargetMinPr: number = 0;
    public newTargetMaxPr: number = 100;
    public newTargetSize: number = null;
    public preferCustomSizes:boolean =false;
    public sizeOptions: TargetSizeRange[] = [];
    public showAddSizeRange: boolean = false;
    public enableSaving: boolean = false;
    public readonly defaultMaterialType = '';
    public selectedMaterialType = this.defaultMaterialType;
    public materialTypeOptions: string[] = [];

    ngOnChanges(changes: SimpleChanges): void {
        if (this.model) {
            if (changes['model'] && changes['model'].previousValue !== changes['model'].currentValue) {
                this.selectedMaterialType = this.defaultMaterialType;
            }
            this.updateMaterialTypeOptions();
            this.loadSelectedTargetSizes();
        }
    }

    async saveSettings() {
        try {
            const sortedSizeOptions = [...this.sizeOptions]?.sort((a, b) => a.targetSize - b.targetSize);
            if (this.selectedMaterialType) {
                const materialTargetConfigs = this.getMaterialTargetConfigs();
                const selectedMaterialConfig: MaterialTargetConfig = {
                    materialType: this.selectedMaterialType,
                    preferCustomSizes: this.preferCustomSizes,
                    targetSizes: sortedSizeOptions,
                };
                const settingIndex = materialTargetConfigs.findIndex(setting => setting.materialType === this.selectedMaterialType);

                if (settingIndex >= 0) {
                    materialTargetConfigs[settingIndex] = selectedMaterialConfig;
                } else {
                    materialTargetConfigs.push(selectedMaterialConfig);
                }

                this.model.materialTargetSizes = JSON.stringify(materialTargetConfigs.sort((a, b) => a.materialType.localeCompare(b.materialType)));
            } else {
                this.model.preferCustomSizes = this.preferCustomSizes;
                this.model.targetSizes = JSON.stringify(sortedSizeOptions);
            }
            await this.dialog.await(this.saveFn(this.model));
            this.snackBar.open('Settings saved successfully', 'OK', {
                duration: 5000,
                panelClass: ['snackbar-success']
            });
            this.fragQuery.trigger$.next();
        } catch (error) {
            console.error('Error saving settings:', error);
        }
    }

    public onMaterialTypeChange(materialType: string) {
        this.selectedMaterialType = materialType ?? this.defaultMaterialType;
        this.loadSelectedTargetSizes();
    }

    private loadSelectedTargetSizes() {
        if (this.selectedMaterialType) {
            const selectedMaterialConfig = this.getMaterialTargetConfigs()
                .find(item => item.materialType === this.selectedMaterialType);
            this.preferCustomSizes = selectedMaterialConfig?.preferCustomSizes ?? false;
            this.sizeOptions = this.cloneTargetSizes(selectedMaterialConfig?.targetSizes ?? []);
            return;
        }

        this.preferCustomSizes = this.model.preferCustomSizes;
        this.sizeOptions = this.parseTargetSizes(this.model.targetSizes);
    }

    private updateMaterialTypeOptions() {
        if (!this.model) {
            this.materialTypeOptions = [];
            this.selectedMaterialType = this.defaultMaterialType;

            return;
        }

        const client = this.getSelectedDeviceClient();
        const clientMaterialTypes = this.parseClientMaterialTypes(client?.materialType)
            .map(item => item.text);

		this.materialTypeOptions = [...new Set(clientMaterialTypes)]
			.filter(Boolean)
            .sort((a, b) => a.localeCompare(b));

        if (this.selectedMaterialType && !this.materialTypeOptions.includes(this.selectedMaterialType)) {
            this.selectedMaterialType = this.defaultMaterialType;
            this.loadSelectedTargetSizes();
        }
    }

    private getSelectedDeviceClient(): Client {
        const selectedDeviceClientId = this.model?.clientId;
        if (selectedDeviceClientId != null) {
            const deviceClient = this.clients.find(client => client.id === selectedDeviceClientId);
            if (deviceClient)
                return deviceClient;

            if (this.loggedInClient?.id === selectedDeviceClientId)
                return this.loggedInClient;
        }

        return this.loggedInClient;
    }

    private parseClientMaterialTypes(materialTypes: string): ClientMaterialType[] {
        const parsed = this.parseJsonArray<ClientMaterialType>(materialTypes);
        return parsed.filter(item => typeof item?.text === 'string' && item.text.trim());
    }

    private getMaterialTargetConfigs(): MaterialTargetConfig[] {
        return this.parseJsonArray<MaterialTargetConfig>(this.model?.materialTargetSizes)
            .filter(setting => typeof setting?.materialType === 'string' && setting.materialType.trim())
            .map(setting => ({
                materialType: setting.materialType,
                preferCustomSizes: !!setting.preferCustomSizes,
                targetSizes: this.cloneTargetSizes(setting.targetSizes ?? []),
            }));
    }

    private parseTargetSizes(targetSizes: string): TargetSizeRange[] {
        return this.cloneTargetSizes(this.parseJsonArray<TargetSizeRange>(targetSizes));
    }

    private cloneTargetSizes(targetSizes: TargetSizeRange[]): TargetSizeRange[] {
        return (Array.isArray(targetSizes) ? targetSizes : [])
            .map(size => ({
                targetMinPr: size.targetMinPr,
                targetMaxPr: size.targetMaxPr,
                targetSize: size.targetSize,
            }));
    }

    private parseJsonArray<T>(value: string): T[] {
        if (!value) {
            return [];
        }

        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }

     addCustomSizeRange() {
        if (this.newTargetSize > 0 && this.newTargetMinPr!=null && this.newTargetMinPr>=0 && this.newTargetMinPr<=100 && this.newTargetMaxPr>=0 && this.newTargetMaxPr<=100 && this.newTargetMaxPr && (this.newTargetMinPr < this.newTargetMaxPr)) {
            const newRange = { targetMinPr: this.newTargetMinPr, targetMaxPr: this.newTargetMaxPr, targetSize: this.newTargetSize };
            this.sizeOptions = [...this.sizeOptions, newRange];
            this.newTargetMinPr=0;
            this.newTargetMaxPr=100;
            this.newTargetSize=null;
        } else {
            alert('Invalid size range');
        }
    }

    removeSizeRange(rangeToRemove: { targetMinPr: number; targetMaxPr: number; targetSize: number }) {
        this.sizeOptions = this.sizeOptions.filter(range => range !== rangeToRemove);
    }
    toggleAddSizeRange() {
        this.showAddSizeRange = !this.showAddSizeRange;
    }

    public ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
